//PrintMap.tsx =================================================
import "../Util/Prototypes";

import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as Print from "./Print";
import * as PrintDataTable from "./PrintDataTable";
import * as PrintMap from "./PrintMap";
import * as PrintUI from "./PrintUI";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as SnackBar from "../Util/SnackBar";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";

import React from "react";

import Extent from "@arcgis/core/geometry/Extent";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

export const legendWidth: number = 2; //Inches.
export let zoomToScale: boolean = false;
export let mapScaleText: string;
export let imageHeight: string;
export let busy: boolean = false;

//Setters.
export function setBusy(busyBee: boolean) {
   busy = busyBee;
}

export function setZoomToScale(zoomToMapScale: boolean) {
   zoomToScale = zoomToMapScale;
}

let printAreaOutline: __esri.Geometry;

let imageWidth: string;
let map_url: string;

let _mapScaleArray: number[];
export let _outScale: number;
let _wdPix: number;
let _htPix: number;
let _wdInch: number;
let _htInch: number;
let _dpi: number;

//Initialize printing.
export function initPrint() {
   createMapScaleArray();
   setPrintParameters();

   MainMap.activeView.on("drag", function () {
      if (DrawerMenu.curToolName === "Print") {
         setPrintParameters();  // console.log("drag state", evt.action);
      }
   });

   reactiveUtils.when(() => MainMap.activeView?.stationary === true, function () {
      if (DrawerMenu.curToolName === "Print") {
         setPrintParameters();
      }
   });
}

//Clear this tool.
export function clearTool() {
   GraLay.temp.removeAll();
}

//Get URL of map image to be printed.
export function getMapURL(): void {

   if (MainMap.view2D) { // 2D
      buildPrintImage();

   } else { // 3D screenshot.
      setPrintParameters();
      const options: __esri.SceneViewTakeScreenshotOptions = {
         height: _htPix,
         width: _wdPix,
      };
      MainMap.activeView.takeScreenshot(options).then(function (results) {
         map_url = results.dataUrl;
         Print.render();
      });
   }
}

//Compute print sizes and map extent.
function setPrintParameters() {
   setPrintSizes();
   setMapExtent();
}

//Compute print sizes.
function setPrintSizes() {
   let pageWidth: number;
   let pageHeight: number;
   let wd: number;
   let ht: number;

   //Full paper size (inches).
   const sa: string[] = PrintUI.paperSize.toLowerCase().split("x");
   pageWidth = Util.parseFlt(sa[1]);
   pageHeight = Util.parseFlt(sa[0]);

   if (PrintUI.portrait) {
      pageWidth = Util.parseFlt(sa[0]);
      pageHeight = Util.parseFlt(sa[1]);
   }

   //Overall size of div element to be printed (inches and points).
   wd = pageWidth - (Settings.print.margin * 2);
   ht = pageHeight - 2; //Subtract top and bottom margins.

   Print.setPageWidth(inchToPt(wd));
   Print.setPageHeight(inchToPt(ht));

   //Legend width adjustment (inches).
   if (PrintUI.includeLegend) {
      wd -= PrintMap.legendWidth;
   }

   //Data table height adjustment.
   let dataGridHt: number = 0;
   const numSel: number = Search.getActiveLayerNumRec();

   if (PrintUI.includeDataTable && numSel > 0) {
      PrintDataTable.buildLableValueList();

      if (numSel === 1) {  //Single feature selected.
         dataGridHt = PrintDataTable.numRow * 0.25; //Custom! Reduce from .28 to .14 as needed to get a taller map.

      } else { //Multiple features selected.
         const lay: Search.iLayer = Search.layers[Search.activeLayerIndex];
         dataGridHt = PrintDataTable.numRow * ((lay.spec.headingFields.length * 0.14) + 0.15);  //Custom! Reduce for taller map.
      }

      if (dataGridHt >= (ht - 2)) {
         ht = 2;
      } else {
         ht -= dataGridHt;
      }
   }

   //Set size variables.
   _wdInch = wd;
   _htInch = ht - 1;

   _dpi = Settings.print.dpi;
   if ((_wdInch * _htInch) > 125) {
      _dpi = 200; //Don't allow high dpi for large prints.
   } else if ((_wdInch * _htInch) > 250) {
      _dpi = 120; //Don't allow high dpi for large prints.
   }

   _wdPix = _wdInch * _dpi;
   _htPix = _htInch * _dpi;

   if (MainMap.activeView.spatialReference.isWebMercator) {
      _wdPix *= Settings.mercFactor;
      _htPix *= Settings.mercFactor;
   }

   imageWidth = inchToPt(_wdInch);  //map img element width (points)
   imageHeight = inchToPt(_htInch); //map img element height (points)
}

//Compute map extent.
function setMapExtent() {
   let i: number;
   let sa: string[];
   let mapScaleFeetPerInch: number;
   const activeView = MainMap.activeView as __esri.MapView;
   const scaleText: string = PrintUI.scaleText;

   //Initialize scale to current map extent (Default).
   _outScale = activeView.scale * 1; //0.999999; //Custom! Reduce value to zoom in a bit.

   const dx: number = activeView.extent.xmax - activeView.extent.xmin;
   const dy: number = activeView.extent.ymax - activeView.extent.ymin;
   mapScaleFeetPerInch = activeView.scale / 12;

   //Use nearest Standard Scale.
   if (scaleText === Settings.print.scales[1]) {

      //Find nearest standard scale.
      const tempScale: number = mapScaleFeetPerInch - (0.02 * mapScaleFeetPerInch);
      mapScaleFeetPerInch = _mapScaleArray[_mapScaleArray.length - 1];

      if (tempScale <= _mapScaleArray[0]) {
         mapScaleFeetPerInch = _mapScaleArray[0];

      } else {
         for (i = 0; i < (_mapScaleArray.length - 1); i++) {
            if ((tempScale > _mapScaleArray[i]) && (tempScale <= _mapScaleArray[i + 1])) {
               mapScaleFeetPerInch = _mapScaleArray[i + 1];
               break;
            }
         }
      }
      _outScale = mapScaleFeetPerInch * 12;

      //Use predefined scale from dropdown list.
   } else if (scaleText !== Settings.print.scales[0]) {
      sa = scaleText.split("=");

      mapScaleFeetPerInch = Util.parseFlt(sa[1]);
      if (scaleText.Contains("ile")) {
         mapScaleFeetPerInch *= 5280;
      }
      _outScale = mapScaleFeetPerInch * 12;
   }

   //Check to see if zoomed in too far.
   let maxScale: number = activeView.constraints.effectiveMaxScale / 12;
   maxScale = Math.round(maxScale);

   if (mapScaleFeetPerInch <= maxScale) {
      mapScaleFeetPerInch = maxScale + 1;
   }

   //Check to see if zoomed out too far.
   let minScale: number = activeView.constraints.effectiveMinScale / 12;
   minScale = Math.round(minScale);

   if (minScale > 0 && mapScaleFeetPerInch >= minScale) {
      mapScaleFeetPerInch = minScale - 1;
   }

   //Set map extent for print page.
   const xCen: number = (dx / 2) + activeView.extent.xmin;
   const yCen: number = (dy / 2) + activeView.extent.ymin;

   mapScaleFeetPerInch = Util_App.feetToMeters(mapScaleFeetPerInch); //For Mercator.

   let xHalf: number = mapScaleFeetPerInch * ((_wdInch + 0.1) / 2); //Add fudge so print border frame does not print.
   let yHalf: number = mapScaleFeetPerInch * ((_htInch + 0.1) / 2);

   if (MainMap.activeView.spatialReference.isWebMercator) {
      xHalf *= Settings.mercFactor;
      yHalf *= Settings.mercFactor;
   }

   const xMin: number = xCen - xHalf;
   const yMin: number = yCen - yHalf;

   const xMax: number = xCen + xHalf;
   const yMax: number = yCen + yHalf;

   const mbr = new Extent({ xmin: xMin, ymin: yMin, xmax: xMax, ymax: yMax, spatialReference: activeView.spatialReference });

   //Create print area graphic.
   const paths = [[[xMin, yMin], [xMin, yMax], [xMax, yMax], [xMax, yMin], [xMin, yMin]]];
   printAreaOutline = new Polyline({ hasZ: false, hasM: false, paths: paths, spatialReference: activeView.spatialReference });

   //Rotate the print graphic.
   if (MainMap.mapView.rotation !== 0) {
      printAreaOutline = geometryEngine.rotate(printAreaOutline, MainMap.mapView.rotation);
   }

   //Set symbology.
   const sym = { type: "simple-line", color: [255, 200, 30, 0.7], width: 5 };
   const gra = new Graphic({ geometry: printAreaOutline, symbol: sym });

   GraLay.temp.removeAll();

   if (MainMap.view2D) {
      GraLay.temp.add(gra); //BUG number: BUG - 000119023 resolved
   }

   //Zoom to print extent when map scale dropdown changed.
   if (PrintMap.zoomToScale) {
      zoomToScale = false;
      activeView.goTo(mbr, { duration: 0 });
   }

   fitSelectedFeatures(gra.geometry.extent);
}

//Fit selected features in the print window.
function fitSelectedFeatures(mbr: __esri.Extent) {
   if (Search.grandTotRec > 0 && PrintUI.printToolActivated && PrintUI.scaleText === Settings.print.scales[0]) {
      PrintUI.setPrintToolActivated(false);

      if (Search.layers[Search.activeLayerIndex].fs.geometryType === "point") {
         return; //Don't zoom to points.
      }

      const activeView = MainMap.activeView;
      let xMin: number = activeView.extent.xmin;
      let yMin: number = activeView.extent.ymin;
      let xMax: number = activeView.extent.xmax;
      let yMax: number = activeView.extent.ymax;

      const dx: number = xMax - xMin;
      let dy: number = yMax - yMin;

      const mbrSel: __esri.Extent = Search.layers[Search.activeLayerIndex].zoomAllMBR;

      const wd: number = mbr.xmax - mbr.xmin; //Width of print fence
      const ht: number = mbr.ymax - mbr.ymin; //Height of print fence.

      const wdSel: number = mbrSel.xmax - mbrSel.xmin; //Width of selected features.
      const htSel: number = mbrSel.ymax - mbrSel.ymin; //Height of selected features.

      let adjustExtent: boolean = false;

      //Zoom out a bit.
      const htMargin: number = Settings.zoomToMarginPercent * htSel * 2;
      const htWithMargin: number = htSel + htMargin;
      if (htWithMargin > ht) {
         adjustExtent = true;
         const overHt: number = ((htWithMargin - ht) / 2);
         yMin -= overHt;
         yMax += overHt;
      }

      const wdMargin: number = Settings.zoomToMarginPercent * wdSel * 2;
      const wdWithMargin: number = wdSel + wdMargin;
      if (wdWithMargin > wd) {
         adjustExtent = true;
         const overWd: number = ((wdWithMargin - wd) / 2);
         xMin -= overWd;
         xMax += overWd;
      }

      //Zoom in a bit.
      if (!adjustExtent) {
         if (htWithMargin < ht) {
            adjustExtent = true;
            let underHt: number = (ht - htWithMargin) / 2;

            if ((underHt * 2) >= dy) {
               //underHt = (dy / 2) - 1;
               underHt = (dy / 3); //Hack.
            }

            yMin += underHt;
            yMax -= underHt;
         }

         if (wdWithMargin < wd) {
            adjustExtent = true;
            let underWd: number = (wd - wdWithMargin) / 2;

            if ((underWd * 2) >= dx) {
               //underWd = (dx / 2) - 1;
               underWd = (dx / 3); //Hack.
            }

            xMin += underWd;
            xMax -= underWd;
         }
      }

      let theSame: boolean = false;
      if (Search.extentAfterSearch) {
         dy = Math.abs(Search.extentAfterSearch.ymin - activeView.extent.ymin);
         if (dy < 1) {
            theSame = true;
         }
      }

      if (adjustExtent && theSame) {
         const mbrNew = new Extent({ xmin: xMin, ymin: yMin, xmax: xMax, ymax: yMax, spatialReference: activeView.spatialReference });
         activeView.goTo(mbrNew, { duration: 0 });
      }
   }
}

//Build the image to be printed.
function buildPrintImage() {

   import("@arcgis/core/rest/support/PrintTemplate").then(({ default: PrintTemplate }) => {
      import("@arcgis/core/rest/support/PrintParameters").then(({ default: PrintParameters }) => {
         import("@arcgis/core/rest/print").then((PrintTask) => {
            Spinner.show();
            busy = true;

            const template = new PrintTemplate({
               format: PrintUI.getFormat(PrintUI.exportFormat) as PrintUI.exportFormatType2D,
               exportOptions: {
                  width: _wdPix,
                  height: _htPix,
                  dpi: _dpi,
               },
               layout: "map-only",
               scalePreserved: true,
               outScale: _outScale,
               layoutOptions: {} //Required hack for 4.19 bug.
            });

            const params = new PrintParameters({
               view: MainMap.mapView,
               template: template,
               outSpatialReference: MainMap.activeView.spatialReference,
            });

            //Util.logMsg("Params: ", params);
            //Util.logMsg("MapView: ", MainMap.mapView);
            PrintTask.execute(Settings.printService_URL, params).then(printResult).catch(printError);

         }); //end import print task.
      }); //end import printParameters.
   }); // end import printTemplate.
}

function printResult(results: __esri.PrintResponse) {
   map_url = results.url;
   setMapScaleText();
   Print.render(); //Print map step 2 of 4.
}

//Print task failed.
function printError(error: Error) {
   Spinner.hide();
   busy = false;
   SnackBar.show("Error creating map image", 3);
   Util.errMsg("Error creating map image", error);
}

//Compute map scale text.
function setMapScaleText() {
   let scale: number = _outScale / 12;
   let scaleTxt: string = scale.toFixed();
   let units: string = "feet";

   if (scale >= 2640) {
      scale = scale / 5280;
      scaleTxt = scale.toFixed(1);
      units = "miles";
      if (scale <= 1) {
         units = "mile";
      }
   }

   mapScaleText = "1 inch = " + scaleTxt + " " + units;
}

//Create array of map scales.
function createMapScaleArray() {
   let i: number;
   let sa: string[];
   let scaleVal: number;

   _mapScaleArray = [];

   for (i = 2; i < Settings.print.scales.length; i++) {
      sa = Settings.print.scales[i].split("=");

      scaleVal = Util.parseFlt(sa[1]);
      if (sa[1].Contains("ile")) {
         scaleVal *= 5280;
      }
      _mapScaleArray.Add(scaleVal);
   }
}

//Inch to point.
function inchToPt(inch: number): string {
   return (inch * 72) + "pt";
}

//Image loaded event.
function imageLoaded() {
   Spinner.hide();
   busy = false;

   if (PrintUI.includeLegend && MainMap.activeView.type === "3d") {
      setTimeout(function () { window.print(); }, 1000); //Wait for legend to render for 3D mode.

   } else { //Print map step 4 of 4.
      //if (navigator.userAgent.indexOf("Firefox") > 0) {
      //   Util_App.openPrintWindow("Print Map", "PrintMap");

      //} else {
         window.print(); // for Chrome, Edge, etc.
      //}
      //To Debug: Comment below line out and set print_div visibility to "visible" in index.html.
      //   if (!root) {
      //      root = ReactDOM.createRoot(document.getElementById(" print_div"));
      //   }
      //   root.render(<div />);
   }
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   return (
      <img src={map_url} onLoad={imageLoaded} alt="Map" style={{ width: imageWidth, height: PrintMap.imageHeight, border: "2px solid gray", margin: 0, padding: 0 }} />
   );

} //end function.
